import { Link } from "react-router-dom";
import { formatGbp } from "../commerce/pricing";
import { productPath, shopCategoryPath } from "../config/routes";
import {
  categoryLabel,
  isProductPhotograph,
  type CatalogueImage,
  type Category,
  type Product
} from "../data/catalogue";

interface CategoryCardProps {
  category: Category;
  linked?: boolean;
  variant?: "feature" | "catalogue";
  count?: number;
  tileImage?: CatalogueImage;
}

export function CategoryCard({
  category,
  linked = true,
  variant = "feature",
  count,
  tileImage
}: CategoryCardProps) {
  const image = tileImage ?? {
    src: category.image,
    alt: category.imageAlt
  };
  const photo = Boolean(tileImage && isProductPhotograph(tileImage));

  if (variant === "catalogue") {
    return (
      <article className="product-card">
        <div className={`product-image${photo ? " product-image-photo" : ""}`}>
          <img src={image.src} alt={image.alt} width="600" height="800" />
        </div>
        <div className="product-card-body">
          <h2>{category.name}</h2>
          <p className="product-meta">
            {category.description}
            {typeof count === "number" ? ` ${count} listings.` : ""}
          </p>
        </div>
      </article>
    );
  }

  const content = (
    <>
      <img src={image.src} alt="" width="800" height="800" loading="lazy" decoding="async" />
      <span className="category-card-content">
        <h3>{category.name}</h3>
        <p>
          {typeof count === "number" ? `${count} preview listings` : category.description}
        </p>
      </span>
    </>
  );

  const className = `category-card category-card-${category.id}${photo ? " category-card-photo" : ""}`;

  return linked ? (
    <Link className={className} to={shopCategoryPath(category.id)}>
      {content}
    </Link>
  ) : (
    <article className={className}>{content}</article>
  );
}

interface ProductCardProps {
  product: Product;
}

export function ProductCard({ product }: ProductCardProps) {
  const productUrl = productPath(product.slug);
  const price = formatGbp(product.retailPricePence);

  return (
    <article className="product-card">
      <Link
        className={`product-image${isProductPhotograph(product.images[0]) ? " product-image-photo" : ""}`}
        to={productUrl}
        aria-label={`View ${product.name}`}
      >
        <img
          src={product.images[0].src}
          alt={product.images[0].alt}
          width="800"
          height="800"
          loading="lazy"
          decoding="async"
        />
        <span className="status-pill">Preview</span>
      </Link>
      <div className="product-card-body">
        <p className="eyebrow">{product.brand}</p>
        <h2>
          <Link to={productUrl}>{product.name}</Link>
        </h2>
        <p className="product-meta">
          {categoryLabel(product)}
          {product.packSize ? ` · ${product.packSize}` : ""}
        </p>
        {price && (
          <p className="preview-price">
            <span className="preview-price-label">Preview price</span>
            <span>{price}</span>
          </p>
        )}
        <Link className="text-link" to={productUrl}>
          View details <span aria-hidden="true">→</span>
        </Link>
      </div>
    </article>
  );
}
